import React, { useId } from 'react';

import { useUIColors } from '../../theme';
import { cx, defineRecipe, useRecipe, type Styleable } from '../../variants';

export const optionCardGroupRecipe = defineRecipe('optionCardGroup', 'ui-option-card-group', {
    variants: {
        framed: { yes: 'ui-option-card-group--framed', no: '' },
    },
    defaultVariants: { framed: 'yes' },
});

export interface OptionCardGroupProps extends Styleable {
    /** The group's own label — the design draws it as the framed card's
     *  header, so it is part of the component, not a caller-side `<legend>`. */
    legend: React.ReactNode;
    /** Framed (default): bordered card, header divided from the options —
     *  the console's "Phạm vi tổ chức" frame. `false` renders a bare label
     *  above the stack, for a group already sitting inside its own card. */
    framed?: boolean;
    /** Validation/hint line under the options, inside the frame. */
    footer?: React.ReactNode;
    children: React.ReactNode;
}

/**
 * The frame around a set of `OptionCard`s: a header, a divider, and the cards
 * stacked full width.
 *
 * EAA-1846: this used to be app-local — a `<fieldset className="apps-scope-radio">`
 * plus a `.apps-scope-radio` rule in the portal's `10-apps.css` — repeated on
 * the create AND the edit screen. That CSS laid the options out on an
 * `auto-fit, minmax(200px, 1fr)` grid, so two options rendered SIDE BY SIDE
 * while the design stacks them full width, and a third option would silently
 * reflow the whole group. Owning the frame here is what makes every group in
 * every app the same shape by construction.
 *
 * `role="group"` + `aria-labelledby`, not `<fieldset>`/`<legend>`: a `<legend>`
 * renders into the fieldset's border notch, and un-notching it (float, or
 * absolute positioning) is a browser-quirk workaround for a header this design
 * draws as a plain divided row. The radios still group by their shared `name`,
 * which is what makes them mutually exclusive — the fieldset never did that.
 */
export function OptionCardGroup({
    legend,
    framed = true,
    footer,
    children,
    className,
    style,
}: OptionCardGroupProps): React.ReactElement {
    const colors = useUIColors();
    const legendId = useId();
    const recipeClass = useRecipe(optionCardGroupRecipe, { framed: framed ? 'yes' : 'no' });

    return (
        <div
            role="group"
            aria-labelledby={legendId}
            className={cx(recipeClass, className)}
            style={{
                display: 'flex',
                flexDirection: 'column',
                border: framed ? `1px solid ${colors.border}` : 'none',
                borderRadius: framed ? 10 : 0,
                background: framed ? colors.surface : 'transparent',
                overflow: 'hidden',
                ...style,
            }}
        >
            <div
                id={legendId}
                style={{
                    padding: framed ? '12px 16px' : '0 0 8px',
                    borderBottom: framed ? `1px solid ${colors.border}` : 'none',
                    fontSize: 14,
                    fontWeight: 600,
                    lineHeight: 1.45,
                    color: colors.text,
                }}
            >
                {legend}
            </div>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 10,
                    padding: framed ? 16 : 0,
                }}
            >
                {children}
                {footer}
            </div>
        </div>
    );
}

export default OptionCardGroup;
